import React, { useEffect, useState, useRef } from 'react';
import { XIcon, HeartIcon, CameraIcon, TimerIcon } from 'lucide-react';
export function AuraMax() {
  // Mock challenges data
  const challenges = [{
    id: '1',
    brand: 'Coca-Cola',
    brandLogo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/ce/Coca-Cola_logo.svg/1200px-Coca-Cola_logo.svg.png',
    description: 'Share a Coke with a friend',
    reward: 100000,
    gradientFrom: 'from-red-500',
    gradientTo: 'to-red-800'
  }, {
    id: '2',
    brand: 'Nike',
    brandLogo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a6/Logo_NIKE.svg/1200px-Logo_NIKE.svg.png',
    description: 'Show us your workout routine',
    reward: 85000,
    gradientFrom: 'from-blue-900',
    gradientTo: 'to-slate-900'
  }, {
    id: '3',
    brand: 'Starbucks',
    brandLogo: 'https://upload.wikimedia.org/wikipedia/en/thumb/d/d3/Starbucks_Corporation_Logo_2011.svg/1200px-Starbucks_Corporation_Logo_2011.svg.png',
    description: 'Create your own coffee art',
    reward: 75000,
    gradientFrom: 'from-green-700',
    gradientTo: 'to-green-900'
  }];
  const [currentChallengeIndex, setCurrentChallengeIndex] = useState(0);
  const cardRef = useRef(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  // New states for challenge acceptance and camera
  const [challengeAccepted, setChallengeAccepted] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [remainingTime, setRemainingTime] = useState(15);
  const [isRecording, setIsRecording] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;
  const handleSwipe = direction => {
    if (currentChallengeIndex < challenges.length - 1) {
      setCurrentChallengeIndex(currentChallengeIndex + 1);
    } else {
      setCurrentChallengeIndex(0);
    }
  };
  const onTouchStart = e => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  const onTouchMove = e => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe || isRightSwipe) {
      handleSwipe(isLeftSwipe ? 'left' : 'right');
    }
  };
  // Handle accepting the challenge
  const handleAcceptChallenge = () => {
    setShowAnimation(true);
    // After animation completes, show the challenge acceptance screen
    setTimeout(() => {
      setShowAnimation(false);
      setChallengeAccepted(true);
    }, 2000); // Animation duration
  };
  // Handle starting the camera
  const handleStartCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user'
        },
        audio: true
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraOpen(true);
      setIsRecording(true);
      setRemainingTime(15);
    } catch (error) {
      console.error('Error accessing camera:', error);
    }
  };
  // Handle stopping the camera
  const handleStopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    setCameraOpen(false);
    setIsRecording(false);
    // Here you would typically save the video or process it
  };
  // Timer for recording
  useEffect(() => {
    let interval;
    if (isRecording && remainingTime > 0) {
      interval = setInterval(() => {
        setRemainingTime(prev => prev - 1);
      }, 1000);
    } else if (isRecording && remainingTime === 0) {
      handleStopCamera();
      // Reset states after recording
      setChallengeAccepted(false);
      // Here you would typically save the recording
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRecording, remainingTime]);
  // Reset if user cancels
  const handleCancel = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    setChallengeAccepted(false);
    setCameraOpen(false);
    setIsRecording(false);
  };
  return <div className="flex flex-col h-full px-4" data-id="element-58">
      <h1 className="text-2xl font-bold text-gray-900 mb-6 pt-4" data-id="element-59">Aura-Max.</h1>
      {/* Full-screen animation overlay */}
      {showAnimation && <div className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-1000 ease-in-out ${showAnimation ? 'opacity-100' : 'opacity-0'}`} data-id="element-60">
          <div className={`absolute inset-0 bg-gradient-to-br ${challenges[currentChallengeIndex].gradientFrom} ${challenges[currentChallengeIndex].gradientTo}`} data-id="element-61"></div>
          <div className="relative z-10 text-white text-center" data-id="element-62">
            <div className="mb-4" data-id="element-63">
              <div className="w-16 h-16 mx-auto rounded-full bg-white/20 flex items-center justify-center" data-id="element-64">
                <div className="w-10 h-10 rounded-full border-2 border-white flex items-center justify-center" data-id="element-65">
                  <span className="text-xl" data-id="element-66">✓</span>
                </div>
              </div>
            </div>
            <h2 className="text-3xl font-bold mb-2" data-id="element-67">Good find!</h2>
            <p className="text-xl font-serif" data-id="element-68">It's in your mind.</p>
          </div>
        </div>}
      {/* Challenge acceptance screen with glass buttons */}
      {challengeAccepted && !cameraOpen && <div className="fixed inset-0 z-40 flex flex-col" data-id="element-69">
          <div className={`absolute inset-0 bg-gradient-to-br ${challenges[currentChallengeIndex].gradientFrom} ${challenges[currentChallengeIndex].gradientTo} opacity-90`} data-id="element-70"></div>
          <div className="relative z-10 flex flex-col items-center justify-center flex-1 px-8 text-white" data-id="element-71">
            <div className="w-16 h-16 mb-6 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center" data-id="element-72">
              <img src={challenges[currentChallengeIndex].brandLogo} alt={challenges[currentChallengeIndex].brand} className="w-10 h-10 object-contain" data-id="element-73" />
            </div>
            <h2 className="text-3xl font-serif text-center mb-8" data-id="element-74">
              {challenges[currentChallengeIndex].description}
            </h2>
            <div className="mb-8" data-id="element-75">
              <span className="text-xl font-medium" data-id="element-76">
                {challenges[currentChallengeIndex].reward.toLocaleString()}{' '}
                <span className="font-bold" data-id="element-77">$AURA</span>
              </span>
            </div>
            <button onClick={handleStartCamera} className="bg-white/40 backdrop-blur-md border border-white/50 text-white font-medium py-3 px-8 rounded-full flex items-center shadow-lg hover:bg-white/30 transition-colors" data-id="element-78">
              <CameraIcon size={18} className="mr-2" data-id="element-79" />
              Start challenge now
            </button>
            <button onClick={handleCancel} className="mt-4 bg-transparent backdrop-blur-md border border-white/30 text-white/80 font-medium py-2 px-6 rounded-full hover:bg-white/10 transition-colors" data-id="element-80">
              Cancel
            </button>
          </div>
        </div>}
      {/* Camera interface with glass UI elements */}
      {cameraOpen && <div className="fixed inset-0 z-40 bg-black" data-id="element-81">
          <video ref={videoRef} autoPlay playsInline className="absolute inset-0 w-full h-full object-cover" data-id="element-82" />
          <div className="absolute top-0 left-0 right-0 p-4 flex justify-between items-center" data-id="element-83">
            <button onClick={handleCancel} className="text-white bg-black/40 backdrop-blur-md border border-white/20 p-2 rounded-full" data-id="element-84">
              <XIcon size={24} data-id="element-85" />
            </button>
            <div className="bg-black/40 backdrop-blur-md border border-white/20 px-4 py-2 rounded-full flex items-center" data-id="element-86">
              <TimerIcon size={16} className="text-red-500 mr-2" data-id="element-87" />
              <span className="text-white font-medium" data-id="element-88">{remainingTime}s</span>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-8 flex justify-center" data-id="element-89">
            <div className="w-16 h-16 rounded-full border-4 border-white/80 backdrop-blur-sm flex items-center justify-center" data-id="element-90">
              <div className="w-12 h-12 bg-red-500 rounded-full animate-pulse" data-id="element-91"></div>
            </div>
          </div>
        </div>}
      {!challengeAccepted && !cameraOpen && <div className="flex-1 flex flex-col items-center justify-center pb-4" data-id="element-92">
          {/* Challenge card - fluid dimensions */}
          <div className="w-full max-w-md" data-id="element-93">
            <div ref={cardRef} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} className="w-full aspect-[3/4] rounded-3xl overflow-hidden relative ios-shadow-lg cursor-grab active:cursor-grabbing" data-id="element-94">
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/80 z-10" data-id="element-95" />
              {/* Brand-specific background gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${challenges[currentChallengeIndex].gradientFrom} ${challenges[currentChallengeIndex].gradientTo} opacity-90`} data-id="element-96" />
              {/* Subtle pattern overlay */}
              <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI1IiBoZWlnaHQ9IjUiPgo8cmVjdCB3aWR0aD0iNSIgaGVpZ2h0PSI1IiBmaWxsPSIjZmZmIj48L3JlY3Q+CjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9IiNjY2MiPjwvcmVjdD4KPC9zdmc+')]" data-id="element-97" />
              {/* Content - fixed padding */}
              <div className="absolute inset-0 flex flex-col justify-between p-6 z-20" data-id="element-98">
                {/* Brand */}
                <div className="flex items-center" data-id="element-99">
                  <div className="w-12 h-12 rounded-full bg-white/40 backdrop-blur-md border border-white/50 shadow-md flex items-center justify-center overflow-hidden" data-id="element-100">
                    <img src={challenges[currentChallengeIndex].brandLogo} alt={challenges[currentChallengeIndex].brand} className="w-7 h-7 object-contain" data-id="element-101" />
                  </div>
                  <span className="ml-4 text-base font-medium text-white" data-id="element-102">
                    {challenges[currentChallengeIndex].brand}
                  </span>
                </div>
                {/* Challenge description - consistent spacing */}
                <div className="flex flex-col items-center mb-8" data-id="element-103">
                  <h2 className="text-2xl font-serif text-center text-white mb-10 max-w-[280px] leading-tight" data-id="element-104">
                    {challenges[currentChallengeIndex].description}
                  </h2>
                  {/* Highlighted AURA value - glass style */}
                  <div onClick={handleAcceptChallenge} className="bg-white/30 backdrop-blur-md border border-white/50 px-8 py-3 rounded-full shadow-lg transform transition-transform active:scale-95 cursor-pointer hover:bg-white/40" data-id="element-105">
                    <span className="text-white font-medium text-xl" data-id="element-106">
                      {challenges[currentChallengeIndex].reward.toLocaleString()}{' '}
                      <span className="font-bold" data-id="element-107">$AURA</span>
                    </span>
                  </div>
                </div>
              </div>
              {/* Swipe instruction indicator - fixed position */}
              <div className="absolute bottom-4 left-0 right-0 flex justify-center z-30" data-id="element-108">
                <div className="text-white/60 text-xs tracking-wide bg-black/20 backdrop-blur-sm px-3 py-1 rounded-full border border-white/20" data-id="element-109">
                  Swipe to see next challenge
                </div>
              </div>
            </div>
          </div>
          {/* Swipe indicator dots - consistent spacing */}
          <div className="flex justify-center mt-6 space-x-2" data-id="element-110">
            {challenges.map((_, index) => <div key={index} className={`w-2 h-2 rounded-full ${index === currentChallengeIndex ? 'bg-blue-500' : 'bg-gray-300'}`} data-id="element-111" />)}
          </div>
        </div>}
    </div>;
}